'use strict'

import Hashids from 'hashids';
import { createSelector } from 'reselect';

import { getUrlParams } from '../app/selectors';
import { getMap as getLocationsMap } from '../locations/selectors';
import { getPageJob, getOrdersMap as getJobsMap } from '../orders/selectors';
import { getPropDailyAttempts, getServeAttempt, getPickupAttempt } from '../attempts/selectors';

export const getAssignmentsMap = ({ assignments }) => assignments;
export const getProps = (_, props) => props;

export const getPropUserAssignments = createSelector(
	[ getAssignmentsMap, getProps ],
	(assignments, { username }) => {
		return assignments[ username ];
	}
);

export const getPageUserAssignments = createSelector(
	[ getAssignmentsMap, getUrlParams ],
	(assignments, { username }) => assignments[ username ]
);

export const getPropUserDayAssignments = createSelector(
	[ getProps, getPropUserAssignments ],
	({ date }, assignments) => {
		return assignments && date ? assignments[ date ] : null;
	}
);

export const getPageUserDayAssignments = createSelector(
	[ getUrlParams, getPageUserAssignments ],
	({ date }, assignments) => {
		return assignments && date ? assignments[ date ] : null;
	}
);

export const getPageUserDayLocations = createSelector(
	[ getPageUserDayAssignments ],
	assignments => assignments ? assignments.locations : []
);

export const getPropUserDayLocations = createSelector(
	[ getPropUserDayAssignments ],
	assignments => assignments ? assignments.locations : []
);

export const getDayStats = createSelector(
	[ getPropUserDayLocations, getPropDailyAttempts ],
	(locations, _attempts) => {

		const init = {
			serves: 0,
			badAddresses: 0,
			attempts: 0,
			cache: {},
		};

		const attempts = _attempts.filter(a => {
			const loc = locations.find(l => l.locationId === a.locationId);

			if (loc) {
				const job = loc.jobs.find(_id => _id === a.jobId);
				return !!job;
			}
		});

		if (!attempts.length) {
			return init;
		}

		return attempts.reduce(
			(s, attempt) => {

				switch (attempt.type) {

					case 'POST':
					case 'SERVE':
						return { ...s, serves: s.serves + 1 };

					case 'BAD_ADDRESS':
						return { ...s, badAddresses: s.badAddresses + 1 };

					case 'ATTEMPT':
						const _cache = {
							...s.cache,
							[ attempt.locationId ]: true,
						};
						return {
							...s,
							attempts: Object.entries(_cache).length,
							cache: _cache,
						};

					default:
						return s;
				}
			},
			init
		);
	}
);

export const getPropUserDayJobIds = createSelector(
	[ getPropUserDayLocations ],
	locations => locations.reduce(
		(ids, { jobs }) => [ ...ids, ...jobs ],
		[]
	)
);

export const getPropUserDayJobsCount = createSelector(
	[ getPropUserDayJobIds ],
	jobIds => jobIds.length
);

export const getPropUserMultiDayAssignments = createSelector(
	[ getProps, getPropUserAssignments ],
	({ dates }, assignments) => dates.reduce(
		(as, d) => ({ ...as, [ d ]: assignments[ d ] }),
		{}
	)
);

export const getLocationPartitions = createSelector(
	[ getLocationsMap, getPageUserDayAssignments ],
	(locationsMap, assignments) => {

		if (!assignments) {
			return [];
		}

		const { locations, partitions } = assignments;

		return partitions.map(
			part => part.map(locationId => locationsMap[ locationId ])
		);
	}
);

const getJob = createSelector(
	[ getProps, getJobsMap ],
	({ jobId }, jobsMap) => jobsMap[ jobId ]
);

export const getPrevDay = createSelector(
	[ getUrlParams, getPageUserAssignments ],
	({ date }, assignments) => {

		if (assignments) {
			const dates = Object.keys(assignments).sort();
			const idx = dates.indexOf(date);

			if (idx > -1) {
				if (idx > 0) {
					return dates[ idx - 1 ];
				}
			}
		}
	}
);

export const getNextDay = createSelector(
	[ getUrlParams, getPageUserAssignments ],
	({ date }, assignments) => {

		if (assignments) {
			const dates = Object.keys(assignments).sort();
			const idx = dates.indexOf(date);

			if (idx > -1) {
				if (idx < dates.length - 1) {
					return dates[ idx + 1 ];
				}
			}
		}
	}
);

export const getNext = createSelector(
	[ getUrlParams, getPageJob, getPageUserDayLocations ],
	({ hashLocationId }, job, locations) => {

		const hashids = new Hashids();
		const locationId = hashids.decodeHex(hashLocationId);

		return locations.reduce(
			(nextL, l, i) => {
				if (!nextL) {
					if (l.locationId === locationId) {

						const jid = l.jobs.reduce(
							(nextJ, _id, j) => {
								if (!nextJ) {
									if (_id === job._id) {
										if (l.jobs[ j + 1 ]) {
											return l.jobs[ j + 1 ];
										} else {
											return null;
										}
									}
								}
								return nextJ;
							},
							null
						);

						if (jid) {
							return {
								locationId,
								jobId: jid,
							};
						} else {

							const loc = locations[ i + 1 ];

							if (loc) {
								return {
									locationId: loc.locationId,
									jobId: loc.jobs[ 0 ],
								};
							}
						}
					}
				}

				return nextL;
			},
			null
		);
	}
);

export const getPrev = createSelector(
	[ getUrlParams, getPageJob, getPageUserDayLocations ],
	({ hashLocationId }, job, locations) => {

		const hashids = new Hashids();
		const locationId = hashids.decodeHex(hashLocationId);

		return locations.reduce(
			(prevL, l, i) => {
				if (!prevL) {
					if (l.locationId === locationId) {

						const jid = l.jobs.reduce(
							(prevJ, _id, j) => {
								if (!prevJ) {
									if (_id === job._id) {
										const _jobId = l.jobs[ j - 1 ];
										if (_jobId) {
											return _jobId;
										} else {
											return null;
										}
									}
								}
								return prevJ;
							},
							null
						);

						if (jid) {
							return {
								locationId,
								jobId: jid,
							};
						} else {

							const loc = locations[ i - 1 ];

							if (loc) {
								return {
									locationId: loc.locationId,
									jobId: loc.jobs[ loc.jobs.length - 1 ],
								};
							}
						}
					}
				}

				return prevL;
			},
			null
		);
	}
);

export const getNextUrl = createSelector(
	[ getUrlParams, getJobsMap, getNext ],
	({ username, date }, jobsMap, nextState) => {

		if (!nextState) {
			return;
		}

		const { locationId, jobId } = nextState;

		const hashids = new Hashids();
		const hash = hashids.encodeHex(locationId);

		if (jobId) {

			const job = jobsMap[ jobId ];

			if (!job) {
				return;
			}

			const companyId = job.companyId;
			const jobid = job.id;

			return `/user/${ username }/${ date }/${ companyId }/${ jobid }/${ hash }`;
		}

		return `/user/${ username }/${ date }/location/${ hash }`;
	}
);

export const getPrevUrl = createSelector(
	[ getUrlParams, getJobsMap, getPrev ],
	({ username, date }, jobsMap, prevState) => {

		if (!prevState) {
			return;
		}

		const { locationId, jobId } = prevState;

		const hashids = new Hashids();
		const hash = hashids.encodeHex(locationId);

		if (jobId) {

			const job = jobsMap[ jobId ];

			if (!job) {
				return;
			}

			const companyId = job.companyId;
			const jobid = job.id;

			return `/user/${ username }/${ date }/${ companyId }/${ jobid }/${ hash }`;
		}

		return `/user/${ username }/${ date }/location/${ hash }`;
	}
);

export const fulfillAssignments = createSelector(
	[ getLocationsMap, getJobsMap, getPageUserDayAssignments ],
	(locationsMap, jobsMap, assignments) => {

		if (assignments) {

			const locations = assignments.locations.map(locObj => ({
				...locObj,
				location: locationsMap[ locObj.locationId ],
				jobs: locObj.jobs.map(id => jobsMap[ id ]),
			}));

			const partitions = assignments.partitions.map(
				part => part.map(locId => locationsMap[ locId ])
			);

			return {
				...assignments,
				locations,
				partitions,
			};
		}
	}
);
